import { ADMIN_ROLES, type AdminRole } from "../../contracts/admin-permissions";
import type { AdminDashboardFirebaseConfig } from "../../app/composition/admin-dashboard-runtime-config";

type FirebaseUserLike = {
  email: string | null;
  getIdToken(forceRefresh?: boolean): Promise<string>;
  getIdTokenResult(forceRefresh?: boolean): Promise<{
    claims: Record<string, unknown>;
  }>;
};

type FirebaseAuthLike = {
  currentUser: FirebaseUserLike | null;
  signOut(): Promise<void>;
};

type FirebaseAppModuleLike = {
  getApp(name?: string): unknown;
  getApps(): unknown[];
  initializeApp(config: AdminDashboardFirebaseConfig, name?: string): unknown;
};

type FirebaseAuthModuleLike = {
  GoogleAuthProvider: new () => { setCustomParameters(parameters: Record<string, string>): void };
  browserPopupRedirectResolver: unknown;
  getAuth(app?: unknown): FirebaseAuthLike;
  inMemoryPersistence: unknown;
  initializeAuth(
    app: unknown,
    options: {
      persistence: unknown;
      popupRedirectResolver: unknown;
    },
  ): FirebaseAuthLike;
  onIdTokenChanged(auth: FirebaseAuthLike, callback: (user: FirebaseUserLike | null) => void | Promise<void>): () => void;
  signInWithPopup(auth: FirebaseAuthLike, provider: unknown): Promise<unknown>;
};

export type FirebaseAdminAuthSnapshot =
  | {
      status: "loading";
      roles: AdminRole[];
    }
  | {
      status: "signed_out";
      roles: AdminRole[];
    }
  | {
      status: "signed_in";
      roles: AdminRole[];
      email?: string;
    }
  | {
      status: "error";
      roles: AdminRole[];
      message: string;
    };

export type FirebaseWebAdminAuthProvider = {
  getAccessToken(): Promise<string | undefined>;
  signIn(): Promise<void>;
  signOut(): Promise<void>;
  subscribe(listener: (snapshot: FirebaseAdminAuthSnapshot) => void): () => void;
};

type FirebaseAdminClaims = {
  adminRoles?: unknown;
  petlog_admin_roles?: unknown;
  "https://petlog.app/admin_roles"?: unknown;
};

const FIREBASE_APP_NAME = "petlog-admin-dashboard";

function normalizeClaimRoles(value: unknown): AdminRole[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is AdminRole => typeof item === "string" && ADMIN_ROLES.includes(item as AdminRole));
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter((item): item is AdminRole => ADMIN_ROLES.includes(item as AdminRole));
  }

  return [];
}

export function extractAdminRolesFromClaims(claims: FirebaseAdminClaims | Record<string, unknown>): AdminRole[] {
  const resolvedRoles = [
    ...normalizeClaimRoles(claims.adminRoles),
    ...normalizeClaimRoles(claims.petlog_admin_roles),
    ...normalizeClaimRoles(claims["https://petlog.app/admin_roles"]),
  ];

  return [...new Set(resolvedRoles)];
}

async function importFirebaseApp(): Promise<FirebaseAppModuleLike> {
  return import("firebase/app") as unknown as Promise<FirebaseAppModuleLike>;
}

async function importFirebaseAuth(): Promise<FirebaseAuthModuleLike> {
  return import("firebase/auth") as unknown as Promise<FirebaseAuthModuleLike>;
}

async function createFirebaseAuth(config: AdminDashboardFirebaseConfig): Promise<{
  auth: FirebaseAuthLike;
  authModule: FirebaseAuthModuleLike;
}> {
  const [firebaseApp, firebaseAuth] = await Promise.all([importFirebaseApp(), importFirebaseAuth()]);
  const app =
    firebaseApp.getApps().find((candidate) => {
      const namedCandidate = candidate as { name?: string };
      return namedCandidate.name === FIREBASE_APP_NAME;
    }) ?? firebaseApp.initializeApp(config, FIREBASE_APP_NAME);

  let auth: FirebaseAuthLike;
  try {
    auth = firebaseAuth.initializeAuth(app, {
      persistence: firebaseAuth.inMemoryPersistence,
      popupRedirectResolver: firebaseAuth.browserPopupRedirectResolver,
    });
  } catch {
    auth = firebaseAuth.getAuth(app);
  }

  return { auth, authModule: firebaseAuth };
}

async function resolveSnapshot(user: FirebaseUserLike | null): Promise<FirebaseAdminAuthSnapshot> {
  if (!user) {
    return {
      status: "signed_out",
      roles: [],
    };
  }

  const tokenResult = await user.getIdTokenResult();
  return {
    status: "signed_in",
    roles: extractAdminRolesFromClaims(tokenResult.claims),
    email: user.email ?? undefined,
  };
}

export async function createFirebaseWebAdminAuthProvider(
  config: AdminDashboardFirebaseConfig,
): Promise<FirebaseWebAdminAuthProvider> {
  const { auth, authModule } = await createFirebaseAuth(config);
  const provider = new authModule.GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });

  return {
    async getAccessToken() {
      return auth.currentUser?.getIdToken();
    },
    async signIn() {
      await authModule.signInWithPopup(auth, provider);
    },
    async signOut() {
      await auth.signOut();
    },
    subscribe(listener) {
      listener({
        status: "loading",
        roles: [],
      });

      return authModule.onIdTokenChanged(auth, async (user) => {
        try {
          listener(await resolveSnapshot(user));
        } catch (error) {
          listener({
            status: "error",
            roles: [],
            message: error instanceof Error ? error.message : "Failed to resolve admin Firebase session.",
          });
        }
      });
    },
  };
}
